import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, ArrowLeft, Check, HelpCircle, Star, ShieldCheck, Home, Sparkles, CheckCircle2,
} from 'lucide-react';
import {
  provinces, propertyTypes, constructionTypes, roofTypes, insurers, generateQuotes,
} from '../mock';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import UI from '../lib/uiCatalog';
import { quotesApi, taxLinkApi, insurersApi } from '../lib/api';
import { useWizardAutoAuth } from '../lib/useWizardAutoAuth';
import InsurerCard from '../components/InsurerCard';
import QuoteFilters from '../components/QuoteFilters';
import SaveQuotePdf from '../components/SaveQuotePdf';
import Seo from '../components/Seo';

const initialData = {
  // Property
  address: '', province: '', municipality: '', urbanisation: '',
  yearBuilt: '', builtM2: '', bedrooms: '', bathrooms: '',
  swimmingPool: false, garage: false, storage: false, garden: false,
  rentalProperty: false, holidayHome: false, permanentResidence: false, vacant: false,
  // Owner
  ownerName: '', ownerEmail: '', ownerPhone: '', ownerNIE: '', ownerCountry: '',
  ownerPassword: '',
  // Current insurance
  insuranceCompany: '', premium: '', renewalDate: '', coverAmount: '', excess: '',
  reasonChanging: '',
  // Construction / property type
  propertyType: '', constructionMaterial: '', roofType: '', solarPanels: false,
  // Security
  securityDoor: false, alarm: false, securityCompany: false, safe: false, barsOnWindows: false,
  // Contents
  furnitureValue: '', jewellery: '', computers: '', electronics: '', art: '', sportsEquipment: '', specialItems: '',
  // Claims
  anyClaims: false, claimsCount: '', claimsDescription: '',
};

export default function HomeInsurance() {
  const [current, setCurrent] = useState(0);
  const [data, setData] = useState(initialData);
  const [saving, setSaving] = useState(false);
  const [taxStatus, setTaxStatus] = useState(null);
  const [prefilling, setPrefilling] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const [insurerProfiles, setInsurerProfiles] = useState({});
  const [filteredQuotes, setFilteredQuotes] = useState(null);
  const quoteListRef = useRef(null);
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const T = (k) => t(k, UI[k]);
  const navigate = useNavigate();

  const auth = useWizardAutoAuth({
    email: data.ownerEmail,
    password: data.ownerPassword,
    name: data.ownerName,
    t, UI,
  });

  const steps = [
    { key: 'property',      label: T('wizard.step_property') },
    { key: 'owner',         label: T('wizard.step_owner') },
    { key: 'construction',  label: T('wizard.step_construction') },
    { key: 'contents',      label: T('wizard.step_contents') },
    { key: 'review',        label: T('wizard.step_review') },
    { key: 'quote',         label: T('wizard.step_quote') },
  ];

  useEffect(() => {
    if (!user) { setTaxStatus(null); return; }
    taxLinkApi.status().then(setTaxStatus).catch(() => setTaxStatus(null));
  }, [user]);

  const prefillFromTax = async () => {
    setPrefilling(true);
    try {
      const p = await taxLinkApi.prefill();
      setData((d) => ({ ...d, ...p }));
      setPrefilled(true);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Could not prefill from EasySpanishTax');
    } finally {
      setPrefilling(false);
    }
  };

  const total = steps.length;
  const progress = ((current + 1) / total) * 100;

  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  const next = async () => {
    const stepKey = steps[current]?.key;
    if (stepKey === 'owner') {
      const ok = await auth.advanceFromOwnerStep();
      if (!ok) return;
    }
    setCurrent((c) => Math.min(c + 1, total - 1));
  };
  const back = () => setCurrent((c) => Math.max(c - 1, 0));

  const quotes = useMemo(
    () => (current === total - 1 ? generateQuotes(data) : []),
    [current, data, total],
  );

  // Fetch insurer profiles once we hit the quote step so the pros/cons render.
  useEffect(() => {
    if (current !== total - 1 || quotes.length === 0) return;
    const names = quotes.map((q) => q.insurer);
    insurersApi.lookup(names, lang).then(setInsurerProfiles).catch(() => setInsurerProfiles({}));
  }, [current, quotes, lang, total]);

  const saveToAccount = async () => {
    if (!user) {
      // stash for after login
      try {
        localStorage.setItem('esi_pending_quote', JSON.stringify({ data, quotes }));
      } catch (_) { /* ignore */ }
      navigate('/login');
      return;
    }
    setSaving(true);
    try {
      await quotesApi.create({ data, quotes, status: 'draft' });
      navigate('/dashboard/quotes');
    } catch (err) {
      alert('Could not save quote: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#f7fafd] min-h-screen">
      <Seo pageKey="home-insurance" />
      <div className="container-x pt-10 pb-4">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
        >
          <ArrowLeft size={14} /> {T('wizard.back')}
        </button>
        <h1 className="mt-3 text-3xl sm:text-4xl font-bold text-[#0b1f3a] tracking-tight">{T('wizard.page_title')}</h1>
        <p className="text-slate-600 mt-2">{T('wizard.page_subtitle')}</p>

        {user && taxStatus?.linked && current === 0 && !prefilled && (
          <div className="mt-4 bg-white border border-[#0d3a8a]/30 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3 shadow-sm">
            <span className="w-10 h-10 rounded-xl bg-[#eef4fb] text-[#0d3a8a] inline-flex items-center justify-center shrink-0">
              <Sparkles size={18} />
            </span>
            <div className="flex-1">
              <p className="font-semibold text-[#0b1f3a] text-sm">
                {T('wizard.tax_found_title')}
              </p>
              <p className="text-xs text-slate-600 mt-0.5">
                {T('wizard.tax_found_body')}
              </p>
            </div>
            <button
              onClick={prefillFromTax}
              disabled={prefilling}
              className="btn-primary !py-2 !px-4 text-sm disabled:opacity-60"
            >
              <Sparkles size={14} /> {prefilling ? T('wizard.prefilling') : T('wizard.prefill_btn')}
            </button>
          </div>
        )}

        {prefilled && (
          <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2 text-sm text-emerald-800">
            <CheckCircle2 size={16} /> {T('wizard.prefilled')}
          </div>
        )}
      </div>

      <div className="container-x pb-16">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Progress bar */}
          <div className="px-6 pt-6">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500 uppercase tracking-wider">
                {T('wizard.step_of').replace('{current}', current + 1).replace('{total}', total)}
              </span>
              <span className="text-xs text-slate-500">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="mt-2 h-2" />
            <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2">
              {steps.map((s, i) => (
                <div
                  key={s.key}
                  className={`progress-step ${
                    i === current ? 'active' : i < current ? 'done' : ''
                  }`}
                >
                  <span className={`step-dot ${i === current ? 'active' : i < current ? 'done' : ''}`}>
                    {i < current ? <Check size={14} /> : i + 1}
                  </span>
                  <span className="hidden sm:inline">{s.label}</span>
                  {i < steps.length - 1 && <span className="text-slate-300 hidden sm:inline">›</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6 pt-8">
            <div className="lg:col-span-2">
              <StepContent
                stepKey={steps[current].key}
                data={data}
                update={update}
                quotes={quotes}
                insurerProfiles={insurerProfiles}
                filteredQuotes={filteredQuotes}
                setFilteredQuotes={setFilteredQuotes}
                quoteListRef={quoteListRef}
                T={T}
                auth={auth}
              />
            </div>
            <aside className="space-y-4">
              <div className="help-box">
                <div className="flex items-start gap-2">
                  <HelpCircle size={18} className="mt-0.5" />
                  <div>
                    <p className="font-semibold">{T('wizard.help_title')}</p>
                    <p className="mt-1 text-slate-700">{T('wizard.help_body')}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-center gap-2 text-[#0d3a8a] font-semibold text-sm">
                  <ShieldCheck size={16} /> {T('wizard.data_safe_title')}
                </div>
                <p className="mt-2 text-xs text-slate-600 leading-relaxed">
                  {T('wizard.data_safe_body')}
                </p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-center gap-2 text-[#0d3a8a] font-semibold text-sm">
                  <Home size={16} /> {T('wizard.saved_title')}
                </div>
                <p className="mt-2 text-xs text-slate-600 leading-relaxed">
                  {T('wizard.saved_body')}
                </p>
              </div>
            </aside>
          </div>

          <div className="border-t border-slate-100 p-5 flex items-center justify-between bg-slate-50">
            <button
              onClick={back}
              disabled={current === 0}
              className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeft size={16} /> {T('wizard.back')}
            </button>
            {current < total - 1 ? (
              <button onClick={next} className="btn-primary">
                {current === total - 2 ? T('wizard.see_quote') : T('wizard.continue')} <ArrowRight size={16} />
              </button>
            ) : (
              <button onClick={saveToAccount} disabled={saving} className="btn-primary disabled:opacity-60">
                {saving ? T('wizard.saving') : (user ? T('wizard.save_account') : T('wizard.save_login'))} <ArrowRight size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="field-label">{label}</Label>
      {children}
    </div>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 cursor-pointer">
      <span className="text-sm text-slate-700">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}

function StepContent({ stepKey, data, update, quotes, insurerProfiles = {}, filteredQuotes, setFilteredQuotes, quoteListRef, T, auth }) {
  switch (stepKey) {
    case 'property':
      return (
        <div className="space-y-5 fade-up">
          <h2 className="text-xl font-semibold text-[#0b1f3a]">{T('wizard.property_title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={T('wf.address')}>
              <Input value={data.address} onChange={(e) => update({ address: e.target.value })} placeholder="Calle Real 12, 3A" />
            </Field>
            <Field label={T('wf.province')}>
              <Select value={data.province} onValueChange={(v) => update({ province: v })}>
                <SelectTrigger><SelectValue placeholder={T('wf.province_ph')} /></SelectTrigger>
                <SelectContent>{provinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={T('wf.municipality')}>
              <Input value={data.municipality} onChange={(e) => update({ municipality: e.target.value })} />
            </Field>
            <Field label={T('wf.urbanisation')}>
              <Input value={data.urbanisation} onChange={(e) => update({ urbanisation: e.target.value })} />
            </Field>
            <Field label={T('wf.year_built')}>
              <Input type="number" value={data.yearBuilt} onChange={(e) => update({ yearBuilt: e.target.value })} placeholder="2005" />
            </Field>
            <Field label={T('wf.built_m2')}>
              <Input type="number" value={data.builtM2} onChange={(e) => update({ builtM2: e.target.value })} placeholder="120" />
            </Field>
            <Field label={T('wf.bedrooms')}>
              <Input type="number" value={data.bedrooms} onChange={(e) => update({ bedrooms: e.target.value })} />
            </Field>
            <Field label={T('wf.bathrooms')}>
              <Input type="number" value={data.bathrooms} onChange={(e) => update({ bathrooms: e.target.value })} />
            </Field>
          </div>

          <div>
            <p className="field-label mb-2">{T('wf.features')}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Toggle label={T('wf.pool')} checked={data.swimmingPool} onChange={(v) => update({ swimmingPool: v })} />
              <Toggle label={T('wf.garage')} checked={data.garage} onChange={(v) => update({ garage: v })} />
              <Toggle label={T('wf.storage')} checked={data.storage} onChange={(v) => update({ storage: v })} />
              <Toggle label={T('wf.garden')} checked={data.garden} onChange={(v) => update({ garden: v })} />
            </div>
          </div>

          <div>
            <p className="field-label mb-2">{T('wf.usage')}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Toggle label={T('wf.rental')} checked={data.rentalProperty} onChange={(v) => update({ rentalProperty: v })} />
              <Toggle label={T('wf.holiday')} checked={data.holidayHome} onChange={(v) => update({ holidayHome: v })} />
              <Toggle label={T('wf.permanent')} checked={data.permanentResidence} onChange={(v) => update({ permanentResidence: v })} />
              <Toggle label={T('wf.vacant')} checked={data.vacant} onChange={(v) => update({ vacant: v })} />
            </div>
          </div>
        </div>
      );
    case 'owner':
      return (
        <div className="space-y-6 fade-up">
          <div>
            <h2 className="text-xl font-semibold text-[#0b1f3a]">{T('wizard.owner_title')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Field label={T('wf.full_name')}><Input value={data.ownerName} onChange={(e) => update({ ownerName: e.target.value })} /></Field>
              <Field label={T('wf.email')}>
                <Input
                  type="email"
                  value={data.ownerEmail}
                  onChange={(e) => update({ ownerEmail: e.target.value })}
                  data-testid="wizard-owner-email"
                />
                {auth.emailStatus.checking && (
                  <p className="text-xs text-slate-500 mt-1">{T('wf.email_checking')}</p>
                )}
                {!auth.isLoggedIn && auth.emailStatus.exists === true && (
                  <p className="text-xs text-emerald-700 mt-1" data-testid="wizard-email-existing">
                    {T('wf.email_existing')}
                  </p>
                )}
              </Field>
              <Field label={T('wf.password')}>
                <Input
                  type="password"
                  value={auth.passwordDisabled ? '' : (data.ownerPassword || '')}
                  onChange={(e) => update({ ownerPassword: e.target.value })}
                  disabled={auth.passwordDisabled}
                  placeholder={auth.passwordPlaceholder}
                  minLength={6}
                  autoComplete="new-password"
                  data-testid="wizard-owner-password"
                />
                {!auth.isLoggedIn && auth.emailStatus.exists === false && (
                  <p className="text-xs text-slate-500 mt-1" data-testid="wizard-password-help">
                    {T('wf.password_help')}
                  </p>
                )}
                {auth.authError && (
                  <p className="text-xs text-rose-600 mt-1" data-testid="wizard-auth-error">
                    {auth.authError}
                  </p>
                )}
              </Field>
              <Field label={T('wf.phone')}><Input value={data.ownerPhone} onChange={(e) => update({ ownerPhone: e.target.value })} /></Field>
              <Field label={T('wf.nie')}><Input value={data.ownerNIE} onChange={(e) => update({ ownerNIE: e.target.value })} /></Field>
              <Field label={T('wf.country')}><Input value={data.ownerCountry} onChange={(e) => update({ ownerCountry: e.target.value })} /></Field>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-6">
            <h3 className="text-lg font-semibold text-[#0b1f3a]">{T('wizard.current_insurance_title')}</h3>
            <p className="text-slate-600 text-sm mt-1">{T('wizard.current_insurance_help')}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Field label={T('wf.insurer')}>
                <Select value={data.insuranceCompany} onValueChange={(v) => update({ insuranceCompany: v })}>
                  <SelectTrigger><SelectValue placeholder={T('wf.insurer_ph')} /></SelectTrigger>
                  <SelectContent>{insurers.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={T('wf.premium')}>
                <Input type="number" value={data.premium} onChange={(e) => update({ premium: e.target.value })} placeholder="420" />
              </Field>
              <Field label={T('wf.renewal_date')}>
                <Input type="date" value={data.renewalDate} onChange={(e) => update({ renewalDate: e.target.value })} />
              </Field>
              <Field label={T('wf.cover_amount')}>
                <Input type="number" value={data.coverAmount} onChange={(e) => update({ coverAmount: e.target.value })} />
              </Field>
              <Field label={T('wf.excess')}>
                <Input type="number" value={data.excess} onChange={(e) => update({ excess: e.target.value })} />
              </Field>
              <Field label={T('wf.reason_change')}>
                <Select value={data.reasonChanging} onValueChange={(v) => update({ reasonChanging: v })}>
                  <SelectTrigger><SelectValue placeholder={T('wf.reason_ph')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cheaper">{T('wf.reason_cheaper')}</SelectItem>
                    <SelectItem value="better-cover">{T('wf.reason_better')}</SelectItem>
                    <SelectItem value="bad-service">{T('wf.reason_bad')}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </div>
        </div>
      );
    case 'construction':
      return (
        <div className="space-y-6 fade-up">
          <div>
            <h2 className="text-xl font-semibold text-[#0b1f3a]">{T('wizard.construction_title')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Field label={T('wf.prop_type')}>
                <Select value={data.propertyType} onValueChange={(v) => update({ propertyType: v })}>
                  <SelectTrigger><SelectValue placeholder={T('wf.prop_type_ph')} /></SelectTrigger>
                  <SelectContent>{propertyTypes.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={T('wf.material')}>
                <Select value={data.constructionMaterial} onValueChange={(v) => update({ constructionMaterial: v })}>
                  <SelectTrigger><SelectValue placeholder={T('wf.material_ph')} /></SelectTrigger>
                  <SelectContent>{constructionTypes.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={T('wf.roof')}>
                <Select value={data.roofType} onValueChange={(v) => update({ roofType: v })}>
                  <SelectTrigger><SelectValue placeholder={T('wf.roof_ph')} /></SelectTrigger>
                  <SelectContent>{roofTypes.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <div className="flex items-center">
                <Toggle label={T('wf.solar')} checked={data.solarPanels} onChange={(v) => update({ solarPanels: v })} />
              </div>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-6">
            <h3 className="text-lg font-semibold text-[#0b1f3a]">{T('wizard.security_title')}</h3>
            <p className="text-slate-600 text-sm mt-1">{T('wizard.security_help')}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              <Toggle label={T('wf.sec_door')} checked={data.securityDoor} onChange={(v) => update({ securityDoor: v })} />
              <Toggle label={T('wf.alarm')} checked={data.alarm} onChange={(v) => update({ alarm: v })} />
              <Toggle label={T('wf.sec_company')} checked={data.securityCompany} onChange={(v) => update({ securityCompany: v })} />
              <Toggle label={T('wf.safe')} checked={data.safe} onChange={(v) => update({ safe: v })} />
              <Toggle label={T('wf.bars')} checked={data.barsOnWindows} onChange={(v) => update({ barsOnWindows: v })} />
            </div>
          </div>
        </div>
      );
    case 'contents':
      return (
        <div className="space-y-6 fade-up">
          <div>
            <h2 className="text-xl font-semibold text-[#0b1f3a]">{T('wizard.contents_title')}</h2>
            <p className="text-slate-600 text-sm mt-1">{T('wizard.contents_help')}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Field label={T('wf.furniture')}>
                <Input type="number" value={data.furnitureValue} onChange={(e) => update({ furnitureValue: e.target.value })} />
              </Field>
              <Field label={T('wf.jewellery')}>
                <Input type="number" value={data.jewellery} onChange={(e) => update({ jewellery: e.target.value })} />
              </Field>
              <Field label={T('wf.computers')}>
                <Input type="number" value={data.computers} onChange={(e) => update({ computers: e.target.value })} />
              </Field>
              <Field label={T('wf.electronics')}>
                <Input type="number" value={data.electronics} onChange={(e) => update({ electronics: e.target.value })} />
              </Field>
              <Field label={T('wf.art')}>
                <Input type="number" value={data.art} onChange={(e) => update({ art: e.target.value })} />
              </Field>
              <Field label={T('wf.sports')}>
                <Input type="number" value={data.sportsEquipment} onChange={(e) => update({ sportsEquipment: e.target.value })} />
              </Field>
              <Field label={T('wf.special')}>
                <Input value={data.specialItems} onChange={(e) => update({ specialItems: e.target.value })} placeholder="e.g. wine collection" />
              </Field>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-6">
            <h3 className="text-lg font-semibold text-[#0b1f3a]">{T('wizard.claims_title')}</h3>
            <div className="flex items-center gap-3 mt-4">
              <Checkbox id="anyClaims" checked={data.anyClaims} onCheckedChange={(v) => update({ anyClaims: !!v })} />
              <Label htmlFor="anyClaims" className="text-sm text-slate-700">{T('wizard.claims_toggle')}</Label>
            </div>
            {data.anyClaims && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <Field label={T('wf.claim_count')}>
                  <Input type="number" value={data.claimsCount} onChange={(e) => update({ claimsCount: e.target.value })} />
                </Field>
                <Field label={T('wf.claim_desc')}>
                  <Textarea value={data.claimsDescription} onChange={(e) => update({ claimsDescription: e.target.value })} rows={3} />
                </Field>
              </div>
            )}
          </div>
        </div>
      );
    case 'review':
      return (
        <div className="space-y-5 fade-up">
          <h2 className="text-xl font-semibold text-[#0b1f3a]">{T('wizard.review_title')}</h2>
          <p className="text-slate-600 text-sm">{T('wizard.review_sub')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <ReviewCard title={T('wizard.step_property')} items={[
              [T('wf.address'), data.address || '—'],
              [T('wf.province'), data.province || '—'],
              [T('wf.built_m2'), data.builtM2 || '—'],
              [T('wf.year_built'), data.yearBuilt || '—'],
              [`${T('wf.bedrooms')} / ${T('wf.bathrooms')}`, `${data.bedrooms || '—'} / ${data.bathrooms || '—'}`],
              [T('wf.pool'), data.swimmingPool ? 'Yes' : 'No'],
            ]} />
            <ReviewCard title={`${T('wizard.step_construction')} & ${T('wizard.security_title')}`} items={[
              [T('wf.prop_type'), data.propertyType || '—'],
              [T('wf.material'), data.constructionMaterial || '—'],
              [T('wf.alarm'), data.alarm ? 'Yes' : 'No'],
              [T('wf.sec_company'), data.securityCompany ? 'Yes' : 'No'],
              [T('wf.safe'), data.safe ? 'Yes' : 'No'],
            ]} />
            <ReviewCard title={T('wizard.step_contents')} items={[
              [T('wf.furniture'), data.furnitureValue ? `€${data.furnitureValue}` : '—'],
              [T('wf.jewellery'), data.jewellery ? `€${data.jewellery}` : '—'],
              [T('wf.electronics'), data.electronics ? `€${data.electronics}` : '—'],
            ]} />
            <ReviewCard title={T('wizard.claims_title')} items={[
              [T('wizard.claims_toggle'), data.anyClaims ? 'Yes' : 'No'],
              [T('wf.claim_count'), data.claimsCount || '—'],
            ]} />
          </div>
        </div>
      );
    case 'quote': {
      const shownQuotes = filteredQuotes ?? quotes;
      return (
        <div className="space-y-5 fade-up">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-xl font-semibold text-[#0b1f3a]">{T('wizard.quotes_title')}</h2>
              <p className="text-slate-600 text-sm mt-1">{T('wizard.quotes_sub')}</p>
            </div>
            <SaveQuotePdf targetRef={quoteListRef} filename="home-insurance-quote.pdf" />
          </div>
          <QuoteFilters
            quotes={quotes}
            insurerProfiles={insurerProfiles}
            product="home"
            onFiltered={setFilteredQuotes}
          />
          <div ref={quoteListRef} className="space-y-3">
            {shownQuotes.map((q, i) => (
              <InsurerCard
                key={q.insurer}
                quote={q}
                profile={insurerProfiles[q.insurer] || null}
                isBest={i === 0}
              />
            ))}
            {shownQuotes.length === 0 && (
              <div className="border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500 text-sm">
                {T('filters.none_match') || 'No insurer matches all selected filters.'}
              </div>
            )}
          </div>
        </div>
      );
    }
    default:
      return null;
  }
}

function ReviewCard({ title, items }) {
  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
      <p className="font-semibold text-[#0b1f3a] text-sm mb-2">{title}</p>
      <ul className="space-y-1">
        {items.map(([k, v]) => (
          <li key={k} className="flex justify-between text-slate-600">
            <span>{k}</span>
            <span className="text-slate-800">{v}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
